import { useState } from "react";
import { Plus, Download, Diamond } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/lib/api";
import { Panel, Table, Pill, useLoad, Loading, FormDialog, act, statusTone } from "@/components/common";

const STATUS = ["not_started", "in_progress", "blocked", "done"];

function Gantt({ tasks }) {
  if (!tasks.length) return null;
  const min = Math.min(...tasks.map((t) => +new Date(t.start)));
  const max = Math.max(...tasks.map((t) => +new Date(t.due))) + 86400000;
  const span = max - min || 1;
  const today = ((Date.now() - min) / span) * 100;
  return (
    <div className="relative space-y-1.5" data-testid="gantt">
      {today > 0 && today < 100 && <div className="absolute inset-y-0 w-px bg-red-400" style={{ left: `calc(12rem + (100% - 12rem) * ${today / 100})` }} />}
      {tasks.map((t) => {
        const l = ((+new Date(t.start) - min) / span) * 100;
        const w = Math.max(((+new Date(t.due) + 86400000 - +new Date(t.start)) / span) * 100, 0.8);
        return (
          <div key={t.id} className="flex items-center gap-2 text-xs">
            <div className="w-48 shrink-0 truncate text-slate-600">{t.wbs} {t.name}</div>
            <div className="relative h-5 flex-1 rounded bg-slate-50">
              {t.milestone ? <Diamond className="absolute top-0.5 h-4 w-4 -translate-x-1/2 fill-indigo-600 text-indigo-600" style={{ left: `${l + w}%` }} />
                : <div className={`absolute h-5 rounded ${t.status === "done" ? "bg-emerald-400" : t.status === "blocked" ? "bg-red-400" : "bg-indigo-400"}`} style={{ left: `${l}%`, width: `${w}%` }}>
                  <div className="h-full rounded bg-black/15" style={{ width: `${t.percent}%` }} /></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Plan({ p, users, reload }) {
  const { data, error, reload: r2 } = useLoad(() => api.get(`/projects/${p.id}/tasks`).then((r) => r.data), [p.id]);
  const [edit, setEdit] = useState(null);
  if (!data) return <Loading error={error} />;
  const canEdit = ["pm", "pgm", "coordinator", "tech_lead", "pmo"].includes(p.my_role) && !p.locked;
  const refresh = () => { r2(); reload(); };
  const fields = [{ name: "name", label: "Task name", required: true }, { name: "wbs", label: "WBS code", help: "e.g. 2.3" }, { name: "workstream", label: "Workstream" },
    { name: "assignee_id", label: "Assignee", type: "select", options: users.map((u) => ({ value: u.id, label: u.name })) },
    { name: "start", label: "Start", type: "date", required: true }, { name: "due", label: "Due", type: "date", required: true, help: p.baselined ? "Baseline dates are kept separately and only change via an approved CR." : "" },
    { name: "status", label: "Status", type: "select", options: STATUS.map((s) => ({ value: s, label: s })) }, { name: "percent", label: "% complete", type: "number" },
    { name: "milestone", label: "Milestone", type: "checkbox", help: "Milestones drive the schedule RAG and gate checklists." }];
  return (
    <>
      <Panel title="Timeline" testid="plan-gantt" action={<div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="export-tasks-btn" onClick={() => downloadFile(`/projects/${p.id}/export/tasks`, `${p.code}_tasks.csv`)}><Download className="mr-1 h-4 w-4" />CSV</Button>
        {canEdit && <Button size="sm" data-testid="add-task-btn" onClick={() => setEdit({ status: "not_started", percent: 0 })}><Plus className="mr-1 h-4 w-4" />Task</Button>}</div>}>
        <Gantt tasks={data} />
      </Panel>
      <Panel title="WBS & schedule" testid="plan-table-panel">
        <Table testid="tasks-table" rows={data} cols={[{ k: "wbs", h: "WBS" }, { k: "name", h: "Task", r: (t) => <span className="font-medium">{t.milestone && "◆ "}{t.name}</span> }, { k: "workstream", h: "Workstream" },
          { k: "assignee_name", h: "Assignee" }, { k: "start", h: "Start" }, { k: "due", h: "Due", r: (t) => <span>{t.due}{t.baseline_due && t.baseline_due !== t.due && <span className="block text-[10px] text-slate-400">baseline {t.baseline_due}</span>}</span> },
          { k: "percent", h: "%", r: (t) => `${t.percent}%` }, { k: "status", h: "Status", r: (t) => <Pill tone={statusTone(t.status)}>{t.status}</Pill> },
          { k: "x", h: "", r: (t) => canEdit && <div className="flex gap-1"><Button size="sm" variant="ghost" data-testid={`edit-task-${t.id}`} onClick={() => setEdit(t)}>Edit</Button>
            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`delete-task-${t.id}`} onClick={() => act(api.delete(`/tasks/${t.id}`), "Deleted").then(refresh)}>Delete</Button></div> }]} />
      </Panel>
      <FormDialog open={!!edit} onOpenChange={() => setEdit(null)} title={edit?.id ? "Edit task" : "New task"} testid="task-form" initial={edit || {}} fields={fields}
        onSubmit={async (v) => {
          const body = Object.fromEntries(fields.map((f) => [f.name, v[f.name]]).filter(([, x]) => x !== undefined && x !== ""));
          if (edit.id) await api.put(`/tasks/${edit.id}`, body); else await api.post(`/projects/${p.id}/tasks`, body);
          refresh();
        }} />
    </>
  );
}
